import { AnyAction } from "redux";
import {
  IdResourceState,
  buildIdResourceActionTypes,
  buildIdResourceActions,
  buildIdResourceReducer,
  buildIdResourceSelectors,
  initialIdResourceState,
} from "../../util/id-resource";

export type Beer = {
  id: string;
  displayName: string;
  appearance: string;
  smell: string;
  taste: string;
  mouthfeel: string;
  imageUrl?: string | null;
};

/**
 * The beers slice holds, per eventId, the resolved catalog beers for that
 * event. This was the last event-scoped slice still on the legacy singleton
 * `loading` + `loadedFor` pattern (one shared flag for every event plus an
 * early-return guard), which is what left the Beers tab stuck on a spinner:
 * a slow or superseded in-flight load kept the singleton flag true with
 * nothing committed to fall back on. Per-event request tokens now handle
 * staleness at commit-time, exactly like answers / results / users / events.
 */
export type BeersState = IdResourceState<Beer[]>;

export const BEERS_ACTION_TYPES = buildIdResourceActionTypes("beers");
export const beersActions = buildIdResourceActions<Beer[]>(BEERS_ACTION_TYPES);
export const beersSelectors = buildIdResourceSelectors<
  { beers: BeersState },
  Beer[]
>((root) => root.beers);

const idResourceReducer = buildIdResourceReducer<Beer[], BeersState>(
  BEERS_ACTION_TYPES
);

const initialState: BeersState = initialIdResourceState<Beer[]>();

// The slice is pure id-resource state: the wrapper only exists to seed the
// initial state. Bottle image URLs are not part of the slice at all — they
// resolve lazily per card (see hooks/use-beer-image-url) as cards scroll
// into view, so Storage traffic never delays or competes with the list.
export default function beersReducer(
  state: BeersState = initialState,
  action: AnyAction
): BeersState {
  return idResourceReducer(state, action);
}
